/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, Button } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ISubscribeViewProps } from '@msdyn365-commerce-modules/subscribe/src/modules/subscribe/./subscribe';
import { ISubscribeProps } from '../definition-extensions/subscribe.ext.props.autogenerated';
import { ISubscribeData } from './subscribe.data';
import SubscribeDataAction, { IPostSubscribe, PostSubscribeInput } from '../../../actions/post-subscribe.action';
import SZFModal from '../../../utilities/szf-modal.component';

/**
 * View component.
 * @param props - The view properties.
 * @returns - Returns nothing.
 */
export const subscribeView: React.FC<ISubscribeViewProps & ISubscribeProps<ISubscribeData>> = props => {
    const { subscribe, subscribeContainer, heading, text, subscribeForm, emailInput, submitButton, resources : {subscriptionErrorMessage} } = props;
    const { alertSuccessModalHeading, alertFailModalHeading, alertFailModalMessage, alertSuccessModalMessage } = props.config;
    const [email, setEmail] = React.useState('');
    const [error, setError] = React.useState('');
    const [showModal, setShowModal] = React.useState(false);
    const [modalType, setModalType] = React.useState(false);
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        const CRMtoken = (await props.data.crmToken)?.token || undefined;
        if (!emailRegex.test(email)) {
            setError(subscriptionErrorMessage);
        } else { 
            setError('');
            const input: IPostSubscribe = {
                CRMtoken,
                email: email
            };
            const createCaseInput = new PostSubscribeInput(input);
            const createCaseResponse = await SubscribeDataAction(createCaseInput, props.context.actionContext);
            if (createCaseResponse.IsSuccess) {
                setShowModal(true)
                setModalType(true) //success
                setEmail('')
            } else {
                setShowModal(true)
                setModalType(false) // fail
            }

        }
    };
    const successImageProps = {
        gridSettings: props.context.request.gridSettings ?? {},
        imageSettings: props.config.successModalImage?.imageSettings,
    };
    const failureImageProps = {
        gridSettings: props.context.request.gridSettings ?? {},
        imageSettings: props.config.failModalImage?.imageSettings,
    };
    const imageContainer = { className: 'ms-content-block__image' };
    const successImage = (
        <Node {...imageContainer}>
            <MsDyn365.Image
                {...props.config.successModalImage}
                {...successImageProps}
                requestContext={props.context.request}
                preserveImageSpace={true}
                editProps={{
                    key: props.config.successModalImage ?? {},
                    requestContext: props.context.request,
                    moduleType: props.typeName,
                    imagePropertyName: 'image',
                    moduleId: props.id,
                }}
                shouldSkipToMainImage
            />
        </Node>
    );
    const failureImage = (
        <Node {...imageContainer}>
            <MsDyn365.Image
                {...props.config.failModalImage}
                {...failureImageProps}
                requestContext={props.context.request}
                preserveImageSpace={true}
                editProps={{
                    key: props.config.failModalImage ?? {},
                    requestContext: props.context.request,
                    moduleType: props.typeName,
                    imagePropertyName: 'image',
                    moduleId: props.id,
                }}
                shouldSkipToMainImage
            />
        </Node>
    );

    return (
        <Module {...subscribe}>
            <Node {...subscribeContainer} className={subscribeContainer.className}>
                {heading}
                {text}
                <Node {...subscribeForm} onSubmit={onSubmit} className={subscribeForm?.className ?? ''}>
                    <input
                        type='email'
                        value={email}
                        onChange={e => e.target.value && setEmail(e.target.value.trim())}
                        className={emailInput?.className}
                        placeholder={props.config.subscribeEmailPlaceholderText}
                    />
                    <Button type='submit' className={submitButton?.className ?? ''}>
                        {props.config.subscribeButtonText}
                    </Button>
                </Node>
                <p className="newsletter-error">{error}</p>
            </Node>
            <SZFModal
                    isOpen={showModal}
                    onModalToggle={() => setShowModal(!showModal)}
                    className={'status-modal'}
                    modalTitle={
                        <>
                            <h3>{modalType ? alertSuccessModalHeading : alertFailModalHeading}</h3>
                            <button onClick={() => setShowModal(!showModal)} className='modal-close-btn'>
                                <img
                                    src='https://images-ap-prod.cms.commerce.dynamics.com/cms/api/rwrswxpkjb/imageFileData/MB2jbv?pubver=0'
                                    alt=''
                                />
                            </button>
                        </>
                    }
                    modalBody={
                        showModal ? (
                            <div className='szf-modal-body'>
                                {modalType ? successImage : failureImage}
                                <h4>{modalType ? alertSuccessModalMessage : alertFailModalMessage}</h4>
                            </div>
                        ) : null
                    }
                />
        </Module>
    );
};

export default subscribeView;
